import { useProductPageStore } from '../../contexts/ProductPageStore'
import styles from './machinelearningqa.module.scss'
import MarkdownLinks from './markdownparsing'

import { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'

import RestRequest from 'Utilities/RestClient/RestClient'

import useFeatureFlag from 'Clutch/Hooks/useFeatureFlag'

import Button from 'Clutch/Molecules/Button'
import Feedback from 'Clutch/Molecules/Feedback/Feedback'

import Card from 'Clutch/Atoms/Card'
import Form from 'Clutch/Atoms/Form'
import Skeleton from 'Clutch/Atoms/Skeleton'
import Typography from 'Clutch/Atoms/Typography'

const delay = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms)
  })

const fetchWithRetry = (url, body, attemptsRemaining = 3) =>
  RestRequest(url, null, body).then((response) => {
    if (response || attemptsRemaining <= 1) return response
    return delay(1000).then(() => fetchWithRetry(url, body, attemptsRemaining - 1))
  })

const MachineLearningQuestionsAndAnswers = () => {
  const isPurchaseable = useProductPageStore((x) => x.isPurchaseable)
  const skuBaseNumber = useProductPageStore((x) => x.skuBaseNumber)
  const [rawQuestion, setRawQuestion] = useState(null)
  const [answer, setAnswer] = useState(null)
  const [loading, setLoading] = useState(false)
  const [enabled, setEnabled] = useState(null)

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0,
    rootMargin: '0px 0px',
  })

  const getIsEnabled = async () => {
    try {
      var response = await RestRequest(`/api/products/smartproductqna/enabled`)
      if (!response) {
        throw `Response from api call was falsey`
      }
      setEnabled(response.enabled)
    } catch (err) {
      console.error(`Error getting enabled status for ai qna`)
    }
  }

  useEffect(() => {
    if (inView && enabled === null && isPurchaseable) {
      getIsEnabled()
    }
  }, [inView])

  if (!enabled) {
    return <div ref={ref}></div>
  }

  const submitForm = async (e) => {
    e.preventDefault()
    if (loading) {
      return
    }
    if (!rawQuestion?.length) {
      return
    }

    setLoading(true)
    console.log('Ai_Qna_Engagement')
    try {
      const request = {
        skuBaseNumber,
        userQuestion: rawQuestion,
      }

      const response = await fetchWithRetry(`/api/products/smartproductqna/getproductanswer`, request)
      if (!response?.isSuccess) {
        throw `Response from api call was non-success`
      }

      setAnswer(response)
    } catch (e) {
      setAnswer({ answer: `Something went wrong, please try that again` })
      console.error(`failed to get ml qa response`, e)
    }
    setLoading(false)
  }

  const onChange = (e) => {
    setRawQuestion(e.target.value)
  }

  return (
    <Card fill dataTestId={'pdp_machinelearning_qa'}>
      <Typography size={1.25} font={'heading'}>
        Ask about this product
      </Typography>
      <Typography size={1} tone={`subtle`}>
        Get an immediate answer with AI
      </Typography>
      <Form className={styles.form} onSubmit={submitForm}>
        <Form.Textbox
          disabled={loading}
          id={'pdp_machinelearning_qa_input'}
          fill
          placeholder={'Type a question'}
          value={rawQuestion}
          onChange={onChange}
        />
        <Button
          className={styles.button}
          size={'small'}
          text={'Get an Answer'}
          intent={'action'}
          type={`submit`}
          segmentEvent={{ optOut: true }}
          disabled={loading}
        />
      </Form>

      <Answer loading={loading} answer={answer} />

      <Typography size={0.75} tone={`subtle`}>
        AI-generated from the information available on product page. To verify or get additional information, please
        contact our Customer Experience team.
      </Typography>
    </Card>
  )
}

const Answer = ({ loading, answer }) => {
  const sendFeedback = async (isPositive) => {
    try {
      const request = {
        questionGuid: answer.questionGuid,
        emotion: isPositive ? `Positive` : `Negative`,
      }

      const response = await RestRequest(`/api/products/smartproductqna/submitfeedback`, null, request)
      if (!response.isSuccess) {
        throw `Server response was non-success`
      }
    } catch (e) {
      console.error(`failed to submit feedback to server`, e)
    }
  }

  if (!loading && !answer) {
    return
  }

  if (loading) {
    return (
      <Card layer={1} fill dataTestId={'pdp_machinelearning_skeleton'}>
        <Skeleton height={16} fullWidth />
        <Skeleton height={16} fullWidth />
        <Skeleton height={16} fullWidth />
      </Card>
    )
  }

  return (
    <>
      <Card layer={1} intent={answer.isSuccess ? null : `negative`} fill dataTestId={'pdp_machinelearning_answer'}>
        <Typography size={1}>
          <MarkdownLinks text={answer.answer} />
        </Typography>
      </Card>
      {answer.isSuccess ? <Feedback hideCopy hideTextFeedback name={`Ai-Qna`} callback={sendFeedback} /> : null}
    </>
  )
}

export default MachineLearningQuestionsAndAnswers
